/**
 * Textarea de Staff (007 · T035; design-system §7 · Input y §8.1, contracts/ui.md §1.4).
 *
 * Mismo aspecto que `Input` (superficie, borde `--fm-border`, radio md, foco coral con anillo
 * visible, inválido con borde `--fm-danger`), texto de **16 px** (evita el zoom automático de iOS)
 * y alto mínimo de 96 px (muy por encima de los 44 px táctiles). Trae su propia etiqueta, ayuda,
 * **contador** de caracteres y error por campo, conectados por `aria-describedby`/`aria-invalid`
 * como `Field`. El contador es `aria-live="polite"` solo cerca del límite, para no anunciar cada
 * tecla.
 */
import { CircleAlert } from 'lucide-react';
import { useId, type Ref, type TextareaHTMLAttributes } from 'react';

import { cn } from './cn';
import { INPUT_CLASSES } from './input';

/** A partir de este resto el contador se anuncia al lector de pantalla. */
const ANNOUNCE_REMAINING = 20;

export interface TextareaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'maxLength'> {
  label: string;
  value: string;
  /** Largo máximo; también fija `maxlength` en el control. */
  maxLength: number;
  help?: string;
  error?: string;
  required?: boolean;
  ref?: Ref<HTMLTextAreaElement>;
  /** `data-testid` del contador (por defecto `textarea-counter`). */
  counterTestId?: string;
}

export function Textarea({
  label,
  value,
  maxLength,
  help,
  error,
  required,
  className,
  id: idProp,
  counterTestId = 'textarea-counter',
  ...props
}: TextareaProps) {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const helpId = help ? `${id}-help` : undefined;
  const counterId = `${id}-counter`;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [helpId, counterId, errorId].filter(Boolean).join(' ');
  const length = value.length;
  const remaining = maxLength - length;

  return (
    <div className="flex flex-col gap-fm-2">
      <label htmlFor={id} className="text-fm-base font-medium text-fm-text">
        {label}
        {required ? (
          <span className="text-fm-primary-text" aria-hidden="true">
            {' *'}
          </span>
        ) : null}
      </label>

      <textarea
        id={id}
        value={value}
        maxLength={maxLength}
        required={required}
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        className={cn(INPUT_CLASSES, 'min-h-24 resize-y py-fm-3', className)}
        {...props}
      />

      <div className="flex items-start justify-between gap-fm-3">
        {help ? (
          <p id={helpId} className="text-fm-sm text-fm-text-secondary">
            {help}
          </p>
        ) : (
          <span />
        )}
        <p
          id={counterId}
          data-testid={counterTestId}
          aria-live={remaining <= ANNOUNCE_REMAINING ? 'polite' : 'off'}
          className={cn(
            'fm-tabular shrink-0 text-fm-sm',
            remaining <= 0 ? 'text-fm-danger-text' : 'text-fm-text-secondary',
          )}
        >
          {`${length}/${maxLength}`}
        </p>
      </div>

      {error ? (
        <p
          id={errorId}
          aria-live="polite"
          className="flex items-center gap-fm-1 text-fm-sm text-fm-danger-text"
        >
          <CircleAlert size={16} aria-hidden="true" />
          {error}
        </p>
      ) : null}
    </div>
  );
}
